import { Behavior } from './behavior';
import { ComponentDefinition } from './component';
import { safeCallback } from './func_arr';
import { TraitBehavior } from './trait_behaviors';
import { isComponent, isElement, isVirtualNode } from './type_symbol';
import { triggerWarning } from './warning';
export var RelationType;
(function (RelationType) {
    RelationType[RelationType["Ancestor"] = 0] = "Ancestor";
    RelationType[RelationType["Descendant"] = 1] = "Descendant";
    RelationType[RelationType["ParentNonVirtualNode"] = 2] = "ParentNonVirtualNode";
    RelationType[RelationType["ChildNonVirtualNode"] = 3] = "ChildNonVirtualNode";
    RelationType[RelationType["ParentComponent"] = 4] = "ParentComponent";
    RelationType[RelationType["ChildComponent"] = 5] = "ChildComponent";
})(RelationType || (RelationType = {}));
const RELATION_TYPE_COUNT = 6;
export const normalizeRelation = (ownerSpace, is, key, relation) => {
    const checkRelationFunc = (f) => {
        if (typeof f === 'function') {
            return f;
        }
        if (f !== undefined) {
            triggerWarning(`the "${key}" relation listener is not a function (when preparing behavior "${is}").`);
        }
        return null;
    };
    let type;
    if (relation.type === 'parent') {
        type = 4 /* RelationType.ParentComponent */;
    }
    else if (relation.type === 'child') {
        type = 5 /* RelationType.ChildComponent */;
    }
    else if (relation.type === 'parent-common-node') {
        type = 2 /* RelationType.ParentNonVirtualNode */;
    }
    else if (relation.type === 'child-common-node') {
        type = 3 /* RelationType.ChildNonVirtualNode */;
    }
    else if (relation.type === 'ancestor') {
        type = 0 /* RelationType.Ancestor */;
    }
    else if (relation.type === 'descendant') {
        type = 1 /* RelationType.Descendant */;
    }
    else {
        const type = relation.type;
        triggerWarning(`the "${key}" relation has an invalid relation type "${type}" (when preparing behavior "${is}").`);
        return null;
    }
    let target = null;
    if (relation.target instanceof ComponentDefinition) {
        target = relation.target.behavior;
    }
    else if (relation.target instanceof Behavior || relation.target instanceof TraitBehavior) {
        target = relation.target;
    }
    else {
        const path = String(relation.target || key);
        const usingTarget = ownerSpace.getBehaviorByUrl(path, is);
        if (usingTarget) {
            target = usingTarget;
        }
        else {
            const globalTarget = ownerSpace.getGlobalUsingComponent(path);
            if (typeof globalTarget === 'object' && globalTarget !== null) {
                target = globalTarget.behavior;
            }
            else {
                target = path;
            }
        }
    }
    if (!target) {
        triggerWarning(`the target of relation "${key}" is not a valid behavior or component (when preparing behavior "${is}").`);
        return null;
    }
    return {
        target,
        type,
        linked: checkRelationFunc(relation.linked),
        linkChanged: checkRelationFunc(relation.linkChanged),
        unlinked: checkRelationFunc(relation.unlinked),
        linkFailed: checkRelationFunc(relation.linkFailed),
    };
};
export const generateRelationDefinitionGroup = (relations) => {
    if (relations === undefined)
        return null;
    const group = {
        definitions: new Array(RELATION_TYPE_COUNT),
        keyMap: Object.create(null),
    };
    const defs = group.definitions;
    const keyMap = group.keyMap;
    const keys = Object.keys(relations);
    for (let i = 0; i < keys.length; i += 1) {
        const key = keys[i];
        const relation = relations[key];
        const relationType = relation.type;
        if (defs[relationType]) {
            keyMap[key] = [relationType, defs[relationType].length];
            defs[relationType].push(relation);
        }
        else {
            keyMap[key] = [relationType, 0];
            defs[relationType] = [relation];
        }
    }
    return group;
};
export const cloneRelationDefinitionGroup = (group) => {
    const newGroup = {
        definitions: group.definitions.slice(),
        keyMap: Object.assign(Object.create(null), group.keyMap),
    };
    return newGroup;
};
export class Relation {
    constructor(associatedComponent, group) {
        this._$comp = associatedComponent;
        const links = new Array(RELATION_TYPE_COUNT);
        if (group) {
            for (let type = 0; type < RELATION_TYPE_COUNT; type += 1) {
                const definitions = group.definitions[type];
                if (definitions) {
                    const link = new Array(definitions.length);
                    for (let i = 0; i < definitions.length; i += 1) {
                        link[i] = null;
                    }
                    links[type] = link;
                }
            }
        }
        this._$group = group;
        this._$sharedGroup = true;
        this._$links = links;
    }
    add(relation) {
        if (this._$sharedGroup) {
            this._$sharedGroup = false;
            const group = this._$group;
            if (group) {
                this._$group = {
                    definitions: group.definitions.slice(),
                    keyMap: Object.assign(Object.create(null), group.keyMap),
                };
            }
            else {
                this._$group = {
                    definitions: new Array(RELATION_TYPE_COUNT),
                    keyMap: Object.create(null),
                };
            }
        }
        const key = Symbol('');
        const defs = this._$group.definitions;
        const keyMap = this._$group.keyMap;
        const relationType = relation.type;
        if (defs[relationType]) {
            keyMap[key] = [relationType, defs[relationType].length];
            defs[relationType].push(relation);
        }
        else {
            keyMap[key] = [relationType, 0];
            defs[relationType] = [relation];
        }
        const linksGroup = this._$links;
        if (linksGroup[relationType] === undefined) {
            linksGroup[relationType] = [null];
        }
        else {
            linksGroup[relationType].push(null);
        }
        return key;
    }
    triggerLinkEvent(parentType, isDetach) {
        var _a, _b;
        const comp = this._$comp;
        const linksGroup = this._$links;
        const selfDefs = (_a = this._$group) === null || _a === void 0 ? void 0 : _a.definitions[parentType];
        if (!selfDefs)
            return;
        for (let i = 0; i < selfDefs.length; i += 1) {
            const links = linksGroup[parentType];
            const oldLink = links[i];
            let newLink = null;
            const def = selfDefs[i];
            const parentBehavior = def.target;
            if (!isDetach) {
                let cur = comp;
                for (;;) {
                    const next = cur.parentNode;
                    if (!next)
                        break;
                    cur = next;
                    if (isVirtualNode(cur)) {
                        continue;
                    }
                    if (isComponent(cur)) {
                        if (cur.hasBehavior(parentBehavior)) {
                            const parentRelation = cur._$relation;
                            if (parentRelation) {
                                let rt;
                                if (parentType === 4 /* RelationType.ParentComponent */) {
                                    rt = 5 /* RelationType.ChildComponent */;
                                }
                                else if (parentType === 0 /* RelationType.Ancestor */) {
                                    rt = 1 /* RelationType.Descendant */;
                                }
                                else {
                                    rt = 3 /* RelationType.ChildNonVirtualNode */;
                                }
                                const parentDefs = (_b = parentRelation._$group) === null || _b === void 0 ? void 0 : _b.definitions[rt];
                                if (parentDefs) {
                                    for (let j = 0; j < parentDefs.length; j += 1) {
                                        const def = parentDefs[j];
                                        if (def.target && this._$comp.hasBehavior(def.target)) {
                                            newLink = {
                                                target: cur,
                                                def,
                                            };
                                            break;
                                        }
                                    }
                                    if (newLink)
                                        break;
                                }
                            }
                        }
                        if (parentType === 4 /* RelationType.ParentComponent */)
                            break;
                    }
                    if (parentType === 2 /* RelationType.ParentNonVirtualNode */)
                        break;
                }
            }
            links[i] = newLink;
            if (oldLink) {
                const oldTarget = oldLink.target;
                const oldDef = oldLink.def;
                if (!newLink || oldLink.target !== newLink.target || oldLink.def !== newLink.def) {
                    if (oldDef.unlinked) {
                        safeCallback('Relation Unlinked Callback', oldDef.unlinked, oldTarget.getMethodCaller(), [comp.getMethodCaller()], oldTarget);
                    }
                    if (def.unlinked) {
                        safeCallback('Relation Unlinked Callback', def.unlinked, comp.getMethodCaller(), [oldTarget.getMethodCaller()], comp);
                    }
                }
                else {
                    if (oldDef.linkChanged) {
                        safeCallback('Relation Link Changed Callback', oldDef.linkChanged, oldTarget.getMethodCaller(), [comp.getMethodCaller()], oldTarget);
                    }
                    if (def.linkChanged) {
                        safeCallback('Relation Link Changed Callback', def.linkChanged, comp.getMethodCaller(), [oldTarget.getMethodCaller()], comp);
                    }
                }
            }
            if (newLink) {
                const newTarget = newLink.target;
                const newDef = newLink.def;
                if (!oldLink || oldLink.target !== newLink.target || oldLink.def !== newLink.def) {
                    if (newDef.linked) {
                        safeCallback('Relation Linked Callback', newDef.linked, newTarget.getMethodCaller(), [comp.getMethodCaller()], newTarget);
                    }
                    if (def.linked) {
                        safeCallback('Relation Linked Callback', def.linked, comp.getMethodCaller(), [newTarget.getMethodCaller()], comp);
                    }
                }
            }
            if (!isDetach && !newLink && def.linkFailed) {
                safeCallback('Relation Link Failed Callback', def.linkFailed, comp.getMethodCaller(), [], comp);
            }
        }
    }
    getLinkedTargets(key) {
        var _a, _b, _c, _d;
        const typeWithIndex = (_a = this._$group) === null || _a === void 0 ? void 0 : _a.keyMap[key];
        if (!typeWithIndex) {
            triggerWarning(`no relation "${String(key)}" found.`);
            return [];
        }
        const [type, index] = typeWithIndex;
        if (type === 4 /* RelationType.ParentComponent */ ||
            type === 2 /* RelationType.ParentNonVirtualNode */ ||
            type === 0 /* RelationType.Ancestor */) {
            const link = (_b = this._$links[type]) === null || _b === void 0 ? void 0 : _b[index];
            if (link)
                return [link.target];
            return [];
        }
        const ret = [];
        const comp = this._$comp;
        const def = (_d = (_c = this._$group) === null || _c === void 0 ? void 0 : _c.definitions[type]) === null || _d === void 0 ? void 0 : _d[index];
        const dfs = (node) => {
            const children = node.childNodes;
            for (let i = 0; i < children.length; i += 1) {
                const child = children[i];
                if (!isElement(child))
                    continue;
                if (isVirtualNode(child)) {
                    dfs(child);
                    continue;
                }
                if (isComponent(child)) {
                    if (child._$relation) {
                        let links;
                        if (type === 5 /* RelationType.ChildComponent */) {
                            links = child._$relation._$links[4 /* RelationType.ParentComponent */];
                        }
                        else if (type === 1 /* RelationType.Descendant */) {
                            links = child._$relation._$links[0 /* RelationType.Ancestor */];
                        }
                        else {
                            links = child._$relation._$links[2 /* RelationType.ParentNonVirtualNode */];
                        }
                        if (links) {
                            for (let i = 0; i < links.length; i += 1) {
                                const link = links[i];
                                if (link && link.target === comp && link.def === def) {
                                    ret.push(child);
                                    break;
                                }
                            }
                        }
                    }
                    if (type === 1 /* RelationType.Descendant */)
                        dfs(child);
                }
                else {
                    if (type === 5 /* RelationType.ChildComponent */ || type === 1 /* RelationType.Descendant */)
                        dfs(child);
                }
            }
        };
        dfs(this._$comp);
        return ret;
    }
}
//# sourceMappingURL=relation.js.map